// <cart-drawer> lives in the header-group section group, so it's present on
// every page from first paint. That means the dialog markup and the cart
// state inside it are always ready to open, with no extra fetch needed just
// to show the drawer.
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('dialog');
    // Section groups assign a dynamic runtime ID (see cart-drawer.liquid's
    // comment); read the real one instead of assuming it matches the file.
    this.sectionId = this.dataset.sectionId;

    // Any link/button anywhere on the page can open the drawer just by
    // carrying this data attribute (see the cart icon in header.liquid).
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    // Delegated rather than bound directly to the button: renderContents()
    // below replaces the dialog's entire innerHTML after every cart change,
    // which would silently destroy a directly-bound listener along with the
    // old button element, leaving a close button that looks right but does
    // nothing the moment the cart re-renders once.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) this.close();
    });

    // Native <dialog> only closes on Escape by default. Clicking the
    // backdrop (i.e. the dialog element itself, since its content sits in a
    // child) should close it too, so we check the click target directly.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    // Quantity/remove forms are re-rendered every time the drawer updates,
    // so we delegate the submit listener to the drawer itself rather than
    // binding to individual forms that get replaced.
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cart-drawer-line-item-form]');
      if (!form) return;
      event.preventDefault();
      this.changeLineItem(form, event.submitter);
    });
  }

  open() {
    this.dialog.showModal();
    // Without this, showModal() auto-focuses the close button (the first
    // focusable element in the dialog), which on iOS Safari shows a
    // native focus ring that isn't reliably suppressed via `outline`.
    // Focusing the dialog itself (tabindex="-1" in the markup makes a
    // non-interactive element focusable) avoids that, and is also the
    // more accessible default: a screen reader lands on the cart's
    // content, not a bare close button, when it opens.
    this.dialog.focus();
  }

  close() {
    this.dialog.close();
  }

  async changeLineItem(form, submitter) {
    const formData = new FormData(form);
    if (submitter && submitter.name === 'remove') {
      formData.set('quantity', '0');
    }

    const body = {
      line: Number(formData.get('line')),
      quantity: Number(formData.get('quantity')),
      // Asking Shopify to re-render this section server-side means we never
      // have to hand-build cart HTML in JS, and it stays in sync with any
      // future changes to cart-drawer.liquid automatically.
      sections: this.sectionId,
    };

    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    this.renderContents(data);
  }

  // Shared entry point for both cart/change.js (above) and cart/add.js
  // (called from product-form.js on a product page), since both return the
  // same { sections, item_count } shape when a `sections` param is passed.
  renderContents(data) {
    if (!data.sections || !data.sections[this.sectionId]) return;

    const parsed = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');
    const newDialog = parsed.querySelector('dialog');
    if (newDialog) {
      this.dialog.innerHTML = newDialog.innerHTML;
    }

    this.updateCartCount(data.item_count);
    this.open();
  }

  updateCartCount(itemCount) {
    if (typeof itemCount !== 'number') return;
    const bubble = document.querySelector('[data-cart-count]');
    if (!bubble) return;
    bubble.textContent = itemCount > 0 ? itemCount : '';
    bubble.hidden = itemCount === 0;
  }
}

customElements.define('cart-drawer', CartDrawer);
